import { useEffect } from "react";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AgeGate } from "./AgeGate";
import { AdminApp } from "./admin/AdminApp";
import { ControlApp } from "./control/ControlApp";
import { DeviceScreen } from "./display/DeviceScreen";
import { PendingScreen } from "./display/PendingScreen";
import { Goontroller } from "./control/screens/Goontroller";
import { ClaimScreen } from "./control/screens/ClaimScreen";
import { RoomScreen } from "./display/RoomScreen";
import { PlayCollection } from "./display/PlayCollection";
import { useSession } from "./lib/session";
import { useRouteAnalytics } from "./lib/useRouteAnalytics";

/** Pageview capture for the SPA. Inside BrowserRouter because it reads useLocation. */
function RouteAnalytics() {
  useRouteAnalytics();
  return null;
}

/**
 * Top-level route split:
 *   /admin/*                  -> admin panel (own password gate; NOT age-gated,
 *                                and intentionally unlinked from the rest of the app)
 *   /view/d/:id               -> a screen: waiting, playing, or disconnected, and nothing
 *                                else. Its device id is in the URL and nowhere else.
 *   /screen                   -> a screen with no device yet: plays a random quick setup
 *                                and displays a code for an operator to redeem.
 *                                `?code=0` hides the code on arrival — same page, but
 *                                entered to watch rather than to pair. It stays claimable
 *                                either way, so the choice is not a commitment.
 *   /view                     -> redirect to /screen; the address it used to live at
 *   /remote                   -> the phone-first remote (a screen reads this path aloud)
 *   /goontroller              -> redirect to /remote; the address it used to live at
 *   /claim/:code              -> a scanned party badge: claims that screen, then /remote
 *
 * `/screen` and `/remote` are the two a HUMAN types — one on the TV, one on the phone —
 * so both are named for the role they produce. `/view/d/:id` keeps its shape because
 * nobody types it: it is minted and navigated to, and its `d` segment is what separates
 * a device from the library at `/views`.
 *   /play/random              -> redirect to /screen?code=0; the two pages collapsed
 *   /play/collection/:id      -> the same, from one collection
 *   /                         -> the front door: the same pending screen as /screen,
 *                                minus the pairing greeting. The playing wall IS the
 *                                pitch; the ☰ carries every role and page.
 *   /welcome                  -> redirect to `/`; the front door lives there now
 *   /*                        -> the control panel (handles its own sub-routes)
 *
 * The screen routes live here rather than under ControlApp because a screen is chrome-free
 * by definition, and ControlApp mounts SiteChrome, SiteFooter and GoonFuel around everything
 * under it. The front door is one of them now, so it sits here by the same rule.
 *
 * The age gate wraps the public surfaces (display + control) but not /admin.
 * React Router ranks by specificity, so /admin/*, /view/... and the static `/` win over /*.
 */
export default function App() {
  // Resolve the user session once for the whole app (every route, incl. the view
  // page) so the nav's sign-in / account control renders everywhere.
  const init = useSession((s) => s.init);
  useEffect(() => {
    void init();
  }, [init]);

  return (
    <BrowserRouter>
      <RouteAnalytics />
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route
          path="/view/d/:id"
          element={
            <AgeGate>
              <DeviceScreen />
            </AgeGate>
          }
        />
        {/* `/screen`, not `/view`. Same reasoning as `/remote` above — it is an address a
            person types on the device in front of them, so it has to say what it makes.
            It also retires a genuine trap: `/view` and `/views` differed by one character
            and meant entirely different things (mint a screen vs the library), which was
            worth its own assertion in role-entry.mts and is now simply not expressible. */}
        <Route
          path="/screen"
          element={
            <AgeGate>
              <PendingScreen />
            </AgeGate>
          }
        />
        {/* The address it used to live at. Redirected, not duplicated. */}
        <Route path="/view" element={<Navigate to="/screen" replace />} />
        {/* The phone-first remote. Mounted HERE rather than inside ControlApp because it
            owns its whole viewport: ControlApp wraps its routes in the site nav, the
            footer and the floating player, and a remote with a nav bar and a picture-in-
            picture over it is the desktop page this exists to not be.

            `/remote`, not `/goontroller`, because a screen across the room now READS this
            path out: "open goonlords.com/remote". A URL that gets spoken and typed has to
            be guessable and spellable, and nobody types "goontroller" correctly from four
            metres away. The page still calls itself the Goontroller — that is the product's
            name for it, and this is only its address. */}
        <Route
          path="/remote"
          element={
            <AgeGate>
              <Goontroller />
            </AgeGate>
          }
        />
        {/* The old address, redirected rather than duplicated — same reasoning as
            /welcome below. It is in the wild: the desktop handoff QR minted links to it. */}
        <Route path="/goontroller" element={<Navigate to="/remote" replace />} />
        {/* Where a scanned PARTY badge lands — see PartyBadge and ClaimScreen. Mounted
            here rather than under ControlApp on purpose: ControlApp wraps its routes in
            SiteChrome, which makes every page it holds a claimable screen wearing a code
            of its own, and a page that can sit on a countdown must not be one. It is the
            same reason the remote uses code-less chrome. */}
        <Route
          path="/claim/:code"
          element={
            <AgeGate>
              <ClaimScreen />
            </AgeGate>
          }
        />
        {/* A GOON ROOM (2026-09-19): the address on the invite link. Chrome-free like
            /screen — the room IS a screen, with the chat beside it. */}
        <Route
          path="/room/:id"
          element={
            <AgeGate>
              <RoomScreen />
            </AgeGate>
          }
        />
        <Route
          path="/play/collection/:id"
          element={
            <AgeGate>
              <PlayCollection />
            </AgeGate>
          }
        />
        {/* `/play/random` minted a throwaway device on a random view. `/screen` already
            plays a random quick setup and is claimable on top of that, so the two collapsed
            into one page and this became the same destination with the code hidden.
            Redirected rather than deleted: it is a real URL that has been linked and
            bookmarked, and a dead one is a black screen for whoever kept it. */}
        <Route path="/play/random" element={<Navigate to="/screen?code=0" replace />} />
        {/* The front door. Every visitor lands here, and it IS the product: the same
            pending screen as /screen — the wall playing, claimable. The welcome card
            that briefly greeted arrivals here is gone: the unified ☰ carries every role
            and page it offered, on every surface. NOT a redirect to /screen: `/` stays
            the canonical home for ads, search and bookmarks, and /screen keeps meaning
            "somebody typed this to pair" — `frontDoor` only quiets the pairing card's
            greeting here. A static path outranks the /* splat below, so ControlApp
            never sees it. */}
        <Route
          path="/"
          element={
            <AgeGate>
              <PendingScreen frontDoor />
            </AgeGate>
          }
        />
        {/* Where the old landing lived. Kept as a redirect rather than a second copy:
            two URLs rendering the same front door is duplicate content to a crawler,
            and this one is in the wild on ad creative and in bookmarks. */}
        <Route path="/welcome" element={<Navigate to="/" replace />} />
        <Route
          path="/*"
          element={
            <AgeGate>
              <ControlApp />
            </AgeGate>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
